import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Button } from './ui';
import { getAssetUrl } from '../utils/urls';
import { getProductCardImage, isPublicCoverPath } from '../utils/productCovers';
import { devError } from '../utils/logger';
import { formatDisplayPrice } from '../utils/currencyDisplay';

const IndicatorCard = ({ indicator }) => {
  const { t, i18n } = useTranslation();
  // Covers are served from CRA public/ — do not route via backend getAssetUrl
  const coverOrDb = getProductCardImage(indicator);
  const imageSrc = isPublicCoverPath(coverOrDb)
    ? coverOrDb
    : getAssetUrl(coverOrDb);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-card border border-white/[.08] bg-surface transition-colors hover:border-white/15">
      <Link to={`/product/${indicator._id}`} className="relative block overflow-hidden">
        <div className="aspect-[16/10] overflow-hidden bg-bg">
          <img
            src={imageSrc}
            alt={indicator.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            onError={(e) => {
              devError('Image load error:', { imageSrc, error: e });
            }}
          />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-6">
        <Link to={`/product/${indicator._id}`}>
          <h3 className="text-lg font-semibold tracking-tight text-body transition-colors group-hover:text-body">
            {indicator.name}
          </h3>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted">
          {indicator.description}
        </p>

        {indicator.features?.length > 0 && (
          <ul className="mt-5 flex-1 space-y-2">
            {indicator.features.slice(0, 3).map((feature, idx) => (
              <li key={idx} className="flex items-start text-sm text-muted">
                <Check className="mr-2 mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                <span className="line-clamp-1">{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-white/[.06] pt-5">
          <span className="text-base font-medium text-body">
            {indicator.price === 0
              ? t('indicators.free')
              : formatDisplayPrice(indicator.price, i18n.language)}
          </span>
          <Button variant="secondary" to={`/product/${indicator._id}`} className="!px-3.5 !py-1.5 !text-[13px]">
            {t('home.products.viewProduct')}
          </Button>
        </div>
      </div>
    </article>
  );
};

export default IndicatorCard;
